import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Table } from 'antd';
import { I18n } from '@aws-amplify/core';
import type { ColumnsType } from 'antd/lib/table';

import { dateFormat } from '@/utils/date-format';
import { EMPTY_VALUE } from '@/views/Bera/constants';
import { DrawerTlids } from '@/views/Bera/_components/DrawerTlids';
import { ModalVersions } from '@/views/Bera/_components/ModalVersions';
import { ColumnActions } from '@/views/Bera/_components/List/ColumnActions';

import type { BeraJobSummaryListItem, BeraVersion } from '@/types/Bera';
import { TlidCell } from './TlidCell';
import * as S from './styles';

const SCORE_DECIMALS = 2;
const ACTIONS_COLUMN_WIDTH = 216;

interface TlidScope {
	report: BeraJobSummaryListItem;
	summaryId: string;
	versionTitle: string | null;
}

interface TableListProps {
	loading: boolean;
	reports: BeraJobSummaryListItem[];
}

export function TableList({ loading, reports }: Readonly<TableListProps>): JSX.Element {
	const [tlidScope, setTlidScope] = useState<TlidScope | null>(null);
	const [versionsReport, setVersionsReport] = useState<BeraJobSummaryListItem | null>(null);

	function handleShowTlids(report: BeraJobSummaryListItem): void {
		setTlidScope({ report, summaryId: report.id, versionTitle: null });
	}

	function handleCloseTlids(): void {
		setTlidScope(null);
	}

	function handleShowVersions(report: BeraJobSummaryListItem): void {
		setVersionsReport(report);
	}

	function handleCloseVersions(): void {
		setVersionsReport(null);
	}

	function handleShowVersionTlids(version: BeraVersion, label: string): void {
		if (!versionsReport) {
			return;
		}

		const date = dayjs(version.created_at).format(dateFormat());
		const versionTitle = `${label} (${date})`;

		setVersionsReport(null);
		setTlidScope({ report: versionsReport, summaryId: version.id, versionTitle });
	}

	const columns: ColumnsType<BeraJobSummaryListItem> = [
		{
			key: 'report_name',
			title: I18n.get('Report'),
			dataIndex: 'report_name',
			align: 'center',
			ellipsis: true
		},
		{
			key: 'workstation_name',
			title: I18n.get('Workstation'),
			dataIndex: 'workstation_name',
			align: 'center',
			ellipsis: true,
			responsive: ['lg'],
			render: (workstationName: string | null) => workstationName ?? EMPTY_VALUE
		},
		{
			key: 'cycle_name',
			title: I18n.get('Cycle'),
			dataIndex: 'cycle_name',
			align: 'center',
			ellipsis: true,
			responsive: ['md'],
			render: (cycleName: string | null) => cycleName ?? EMPTY_VALUE
		},
		{
			key: 'tlid_count',
			title: I18n.get('TLID'),
			dataIndex: 'tlid_count',
			align: 'center',
			render: (_value, report) => <TlidCell report={report} onShowTlids={handleShowTlids} />
		},
		{
			key: 'overall_score',
			title: I18n.get('RPN'),
			dataIndex: 'overall_score',
			align: 'center',
			responsive: ['sm'],
			render: (score: number | null) => score?.toFixed(SCORE_DECIMALS) ?? EMPTY_VALUE
		},
		{
			key: 'updated_at',
			title: I18n.get('Last update'),
			dataIndex: 'updated_at',
			align: 'center',
			responsive: ['lg'],
			render: (updatedAt: string) => dayjs(updatedAt).format(dateFormat())
		},
		{
			key: 'actions',
			title: I18n.get('Actions'),
			align: 'center',
			width: ACTIONS_COLUMN_WIDTH,
			render: (_value, report) => <ColumnActions report={report} onShowVersions={handleShowVersions} />
		}
	];

	return (
		<S.TableWrapper span={24}>
			<Table
				rowKey="id"
				columns={columns}
				loading={loading}
				dataSource={reports}
				pagination={false}
				showSorterTooltip={false}
			/>
			{!!tlidScope && (
				<DrawerTlids
					open
					report={tlidScope.report}
					summaryId={tlidScope.summaryId}
					versionTitle={tlidScope.versionTitle}
					onClose={handleCloseTlids}
				/>
			)}
			{!!versionsReport && (
				<ModalVersions
					open
					report={versionsReport}
					onCancel={handleCloseVersions}
					onShowTlids={handleShowVersionTlids}
				/>
			)}
		</S.TableWrapper>
	);
}
